"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import CarCard from "@/app/components/CarCard";
import SelectDropdown from "@/app/components/SelectDropdown";
import type { Car } from "@/types/Car";

const REGIONS = ["Northern", "Central", "Eastern", "Southern"] as const;
type Region = (typeof REGIONS)[number];

const REGION_TO_DISTRICTS: Record<Region, string[]> = {
  Northern: [
    "Chitipa",
    "Karonga",
    "Rumphi",
    "Nkhata Bay",
    "Mzuzu",
    "Likoma",
    "Mzimba",
  ],
  Central: [
    "Kasungu",
    "Nkhotakota",
    "Ntchisi",
    "Dowa",
    "Salima",
    "Lilongwe",
    "Mchinji",
    "Dedza",
    "Ntcheu",
  ],
  Eastern: ["Mangochi", "Machinga", "Balaka", "Zomba"],
  Southern: [
    "Blantyre",
    "Thyolo",
    "Chiradzulu",
    "Mwanza",
    "Neno",
    "Mulanje",
    "Phalombe",
    "Chikwawa",
    "Nsanje",
  ],
};

type AvailabilityResult = {
  car: Car;
  available: boolean;
};

export default function Hero() {
  const fallbackImages = [
    "/images/corousel/image.png",
    "/images/corousel/image1.png",
    "/images/corousel/image2.png",
    "/images/corousel/image3.png",
    "/images/corousel/image4.png",
  ];

  const [heroImages, setHeroImages] = useState<string[]>(fallbackImages);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const prevIndexRef = useRef(0);
  const [region, setRegion] = useState<Region | "">("");
  const [district, setDistrict] = useState("");
  const [carQuery, setCarQuery] = useState("");

  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<AvailabilityResult[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [carSuggestions, setCarSuggestions] = useState<string[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages]);

  useEffect(() => {
    setFadeIn(false);
    const raf = requestAnimationFrame(() => setFadeIn(true));
    return () => {
      cancelAnimationFrame(raf);
      prevIndexRef.current = currentIndex;
    };
  }, [currentIndex]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.NEXT_PUBLIC_API_BASE_URL;
        if (!base) return;
        const res = await axios.get(`${base}/cars`);
        const cars = (res.data?.data ?? res.data) as Car[];
        const list = Array.isArray(cars) ? cars : [];

        const names = list
          .map((c) => String(c?.name || "").trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        if (!cancelled) setCarSuggestions(names);

        const availableImages = list
          .filter((c) => c.status === "Available" && c.image)
          .map((c) => c.image);
        if (!cancelled && availableImages.length > 0) {
          setHeroImages(availableImages);
          setCurrentIndex(0);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function normalize(s: string) {
    return s.trim().toLowerCase();
  }

  const canSearch = Boolean(region && district.trim());

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setResults(null);

    const base = process.env.NEXT_PUBLIC_API_BASE_URL;
    if (!base) {
      setMessage("Search is unavailable (missing API base URL).");
      return;
    }

    if (!canSearch) {
      setMessage("Please choose a region and district.");
      return;
    }

    setIsSearching(true);
    try {
      const res = await axios.get(`${base}/cars`);
      const cars = (res.data?.data ?? res.data) as Car[];
      const list = Array.isArray(cars) ? cars : [];

      const q = normalize(carQuery);
      const filtered = q
        ? list.filter((c) => normalize(String(c?.name || "")).includes(q))
        : list;

      const available = filtered
        .filter((c) => normalize(String(c?.status || "")) !== "booked")
        .map((car) => ({ car, available: true }));

      if (available.length === 0) {
        setMessage(
          carQuery.trim()
            ? `"${carQuery.trim()}" not available right now.`
            : "No cars available right now.",
        );
      } else {
        setMessage(null);
      }

      setResults(available.slice(0, 8));

      setCarSuggestions((prev) => {
        const availableNames = available
          .map((r) => String(r.car?.name || "").trim())
          .filter(Boolean);
        if (availableNames.length === 0) return prev;
        return Array.from(new Set([...availableNames, ...prev])).slice(0, 50);
      });
    } finally {
      setIsSearching(false);
    }
  }
  return (
    <section className="relative hero-bg text-white">
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent" />

      <div className="relative hero-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24">
        {/* Hero top: headline + carousel */}
        <div className="flex flex-col gap-4 sm:gap-6 md:flex-row md:items-center md:gap-12">
          {/* Left: Headline */}
          <div className="w-full md:w-1/2">
            <span className="hero-fade-up inline-flex items-center gap-2 mb-2 sm:mb-3 text-[10px] sm:text-lg font-bold uppercase tracking-widest text-blue-300 bg-blue-900/60 px-3 py-1 rounded-full border border-blue-700/50">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-400" />
              </span>
              #1 Car Rental in Malawi
            </span>
            <h1 className="hero-fade-up hero-fade-up-delay-1 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
              Rent a Car for Your Next{" "}
              <span className="hero-gradient-text">Adventure</span>
            </h1>
            <p className="hero-fade-up hero-fade-up-delay-2 mt-2 sm:mt-3 text-xs sm:text-sm md:text-base text-white/80 leading-relaxed">
              Explore Malawi and beyond with the best rental deals. Transparent
              pricing, no hidden fees.
            </p>

            {/* <div className="mt-3 sm:mt-5 flex flex-wrap gap-2 sm:gap-3">
              {["No Hidden Fees", "Free Cancellation", "24/7 Support"].map(
                (badge) => (
                  <span
                    key={badge}
                    className="text-[10px] sm:text-xs font-semibold text-white/90 bg-white/10 border border-white/20 px-2 sm:px-3 py-1 rounded-full"
                  >
                    ✓ {badge}
                  </span>
                ),
              )}
            </div> */}
          </div>

          <div className="hero-fade-up hero-fade-up-delay-1 w-full md:w-1/2">
            <div className="relative h-32 sm:h-44 md:h-56 lg:h-72 rounded-xl overflow-hidden shadow-2xl">
              <Image
                src={
                  heroImages[prevIndexRef.current] ?? heroImages[currentIndex]
                }
                alt=""
                fill
                aria-hidden="true"
                className="object-cover"
              />
              <Image
                src={heroImages[currentIndex]}
                alt="Hero car"
                fill
                priority
                className="object-cover transition-opacity duration-700 ease-out"
                style={{ opacity: fadeIn ? 1 : 0 }}
              />
              {/* Dot indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {heroImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    aria-label={`Slide ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === currentIndex ? "w-4 bg-white" : "w-1.5 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Search Card */}
        <div className="hero-fade-up hero-fade-up-delay-3 search-card mt-6 bg-white/10 backdrop-blur-md p-4 md:p-6 rounded-xl shadow-lg transition-shadow duration-300 hover:shadow-2xl">
          <form
            onSubmit={handleSearch}
            className="grid grid-cols-1 gap-3 md:grid-cols-3 items-end"
          >
            {/* Region + District */}
            <div className="grid grid-cols-2 gap-2">
              <SelectDropdown
                value={region}
                onChange={(v) => {
                  setRegion(v as Region);
                  setDistrict("");
                }}
                options={[...REGIONS]}
                placeholder="Region"
              />

              <SelectDropdown
                value={district}
                onChange={setDistrict}
                options={region ? REGION_TO_DISTRICTS[region] : []}
                placeholder="District"
                disabled={!region}
                disabledPlaceholder="Choose region"
              />
            </div>

            {/* Car */}
            <input
              id="carQuery"
              list="carSuggestions"
              placeholder="Car (type to search)"
              value={carQuery}
              onChange={(e) => setCarQuery(e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm md:text-base rounded-md border border-gray-300"
            />
            {/* BUTTON */}
            <button
              type="submit"
              disabled={isSearching || !canSearch}
              title={
                !canSearch ? "Choose a region and district first" : undefined
              }
              className={`
                w-full md:w-auto
                px-4 py-2 md:px-5 md:py-2.5
                text-xs sm:text-sm md:text-base
                font-semibold
                rounded-md
                bg-blue-900 hover:bg-blue-700
                text-white
                transition
                shadow
                disabled:opacity-40
                disabled:cursor-not-allowed
                disabled:hover:bg-blue-900
              `}
            >
              {isSearching ? "Searching..." : "Search"}
            </button>
          </form>

          {/* Suggestions */}
          <datalist id="carSuggestions">
            {carSuggestions.slice(0, 30).map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {message && (
            <div className="mt-3 rounded-md bg-white/10 px-3 py-2 text-xs sm:text-sm text-white">
              {message}
            </div>
          )}
        </div>

        {/* Results */}
        {results && results.length > 0 && (
          <div className="mt-6 sm:mt-8 rounded-lg bg-white/5 p-3 sm:p-4">
            <h2 className="text-base sm:text-lg font-extrabold text-white">
              Search Results
            </h2>
            <p className="mt-1 text-[10px] sm:text-xs text-white/80">
              Showing available cars for {district}
            </p>
            <div className="mt-3 sm:mt-4 grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {results.map((r) => (
                <CarCard key={r.car._id} car={r.car} />
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .hero-fade-up {
          opacity: 0;
          transform: translateY(16px);
          animation: heroFadeUp 0.8s ease-out forwards;
        }
        .hero-fade-up-delay-1 {
          animation-delay: 0.15s;
        }
        .hero-fade-up-delay-2 {
          animation-delay: 0.3s;
        }
        .hero-fade-up-delay-3 {
          animation-delay: 0.45s;
        }
        @keyframes heroFadeUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .hero-gradient-text {
          background-image: linear-gradient(
            90deg,
            #93c5fd,
            #60a5fa,
            #3b82f6,
            #60a5fa,
            #93c5fd
          );
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: heroGradientShift 4s linear infinite;
        }
        @keyframes heroGradientShift {
          to {
            background-position: 200% center;
          }
        }
      `}</style>
    </section>
  );
}
